/**
 * Unified Leave Setup Hub
 *
 * All four setup sections (Leave Year, Leave Types, Entitlement Rules,
 * Holiday Calendar) live on ONE page so the user never accidentally
 * creates the wrong thing.  Each section has its own inline form that
 * expands when "New" is clicked, plus a compact list of existing items
 * and a "Manage all →" link to the full dedicated page.
 */
import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  CalendarDays,
  CalendarRange,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FileSpreadsheet,
  ListChecks,
  Plus,
  Settings,
  X,
} from "lucide-react";

import { leaveApi } from "../api";
import { useToast } from "#/components/common/ToastProvider";
import { useConfirm } from "#/components/common/ConfirmProvider";
import { LeaveBackButton } from "../components/LeaveBackButton";
import { LeaveError } from "../components/LeaveError";
import { LeaveLoading } from "../components/LeaveLoading";
import { LeavePageHeader } from "../components/LeavePageHeader";
import { LeaveTypeForm } from "../components/LeaveTypeForm";
import { useAsyncData, useMutation } from "../hooks";
import type { LeaveBalanceRecalculationResponse } from "../types";
import type {
  CarryForwardExpiryMode,
  EmployeeOption,
  Holiday,
  HolidayCreateRequest,
  LeaveApplicableGender,
  LeaveEntitlementRule,
  LeaveEntitlementRuleCreateRequest,
  LeaveDayCountMode,
  LeaveType,
  LeaveTypeCreateRequest,
  LeaveYearPolicy,
  LeaveYearPolicyCreateRequest,
  RefLookup,
} from "../types";

// ─────────────────────────────────────────────────────────────────────────────
// Page root
// ─────────────────────────────────────────────────────────────────────────────
export default function LeaveSetupPage() {
  const setup = useAsyncData(() => leaveApi.setupStatus(), []);

  const steps = [
    { label: "Leave Year", done: setup.data?.hasLeaveYearPolicy },
    { label: "Leave Types", done: setup.data?.hasLeaveTypes },
    { label: "Entitlement Rules", done: setup.data?.hasEntitlementRules },
    { label: "Holiday Calendar", done: setup.data?.hasHolidays },
  ];
  const doneCount = steps.filter((s) => s.done).length;

  return (
    <div className="space-y-6">
      <LeavePageHeader
        title="Leave Setup Hub"
        actions={<LeaveBackButton to="/hr/leave" label="Dashboard" />}
      />

      {/* Progress bar */}
      {!setup.loading && (
        <div className="rounded-3xl border bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center justify-between text-sm">
            <span className="font-black text-slate-900">Setup Progress</span>
            <span className={`font-bold ${doneCount === 4 ? "text-emerald-600" : "text-amber-600"}`}>
              {doneCount} / 4 complete
              {doneCount === 4 ? " ✓" : ""}
            </span>
          </div>
          <div className="flex gap-1.5">
            {steps.map((step, i) => (
              <div key={i} className="flex-1">
                <div
                  className={`h-2 rounded-full transition-all ${
                    step.done ? "bg-emerald-500" : "bg-slate-200"
                  }`}
                />
                <div className={`mt-1.5 text-center text-xs font-semibold ${
                  step.done ? "text-emerald-600" : "text-slate-400"
                }`}>
                  {step.label}
                </div>
              </div>
            ))}
          </div>
          {doneCount === 4 && (
            <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700">
              ✓ All setup complete — employees can now apply for leave.
            </div>
          )}
        </div>
      )}

      {setup.loading && <LeaveLoading label="Checking setup status…" />}
      {setup.error && <LeaveError message={setup.error} onRetry={setup.reload} />}

      {/* ── Section 1: Leave Year ─────────────────────────────────────── */}
      <LeaveYearSection onComplete={setup.reload} done={setup.data?.hasLeaveYearPolicy} />

      {/* ── Section 2: Leave Types ───────────────────────────────────── */}
      <LeaveTypesSection onComplete={setup.reload} done={setup.data?.hasLeaveTypes} />

      {/* ── Section 3: Entitlement Rules ─────────────────────────────── */}
      <EntitlementRulesSection onComplete={setup.reload} done={setup.data?.hasEntitlementRules} />

      {/* ── Section 4: Holiday Calendar ──────────────────────────────── */}
      <HolidaysSection onComplete={setup.reload} done={setup.data?.hasHolidays} />

      {/* Employee Balances link (not a setup step, but related) */}
      <div className="rounded-2xl border bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100">
              <FileSpreadsheet className="h-4 w-4 text-slate-500" />
            </div>
            <div>
              <div className="font-black text-slate-900">Employee Leave Balances</div>
              <div className="text-xs text-slate-500">View and manage leave balances for all employees.</div>
            </div>
          </div>
          <Link
            to="/hr/leave/balances"
            className="rounded-xl border px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
          >
            View Balances →
          </Link>
        </div>

        <RecalculateBalancesAction />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Recalculate All Leave Balances
// ─────────────────────────────────────────────────────────────────────────────
function RecalculateBalancesAction() {
  const toast = useToast();
  const confirm = useConfirm();
  const [result, setResult] = useState<LeaveBalanceRecalculationResponse | null>(null);
  const run = useMutation(() => leaveApi.recalculateBalances());

  async function handleClick() {
    if (
      !(await confirm(
        "Create any missing leave balance rows for every active employee, based on the leave types and entitlement rules configured right now? Existing balances are left untouched.",
      ))
    ) {
      return;
    }

    try {
      const response = await run.mutate();
      setResult(response);
      if (response.employeesFailed > 0) {
        toast.error(`Completed with ${response.employeesFailed} employee(s) failing — see details below.`);
      } else {
        toast.success("Leave balances recalculated for all active employees.");
      }
    } catch {
      // run.error already surfaces the message below
    }
  }

  return (
    <div className="mt-3 border-t pt-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm font-bold text-slate-800">Recalculate All Leave Balances</div>
        </div>
        <button
          type="button"
          onClick={handleClick}
          disabled={run.loading}
          className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800 disabled:opacity-60"
        >
          {run.loading ? "Recalculating…" : "Recalculate for All Employees"}
        </button>
      </div>

      {run.error && <LeaveError message={run.error} />}

      {result && (
        <div
          className={`mt-3 rounded-xl border px-4 py-3 text-sm ${
            result.employeesFailed > 0
              ? "border-amber-200 bg-amber-50 text-amber-800"
              : "border-emerald-200 bg-emerald-50 text-emerald-800"
          }`}
        >
          <div className="font-bold">{result.message}</div>
          <div className="mt-1 text-xs">
            Leave year {result.leaveYear ?? "—"} · {result.employeesProcessed} employee(s) processed ·{" "}
            {result.balancesCreatedOrVerified} balance(s) created or verified
            {result.employeesFailed > 0 ? ` · ${result.employeesFailed} failed` : ""}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared Section wrapper
// ─────────────────────────────────────────────────────────────────────────────
function SectionCard({
  step,
  icon: Icon,
  title,
  description,
  done,
  managePath,
  manageLabel,
  showForm,
  onToggleForm,
  addLabel,
  formError,
  listContent,
  formContent,
}: {
  step: number;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  done?: boolean;
  managePath: string;
  manageLabel: string;
  showForm: boolean;
  onToggleForm: () => void;
  addLabel: string;
  formError?: string | null;
  listContent: React.ReactNode;
  formContent: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-3xl border bg-white shadow-sm">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 border-b bg-slate-50 px-5 py-4">
        <div className="flex items-start gap-3">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-sm font-black ${
            done ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
          }`}>
            {done ? <CheckCircle2 className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Step {step}</span>
              <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${
                done ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
              }`}>
                {done ? "Configured" : "Not configured"}
              </span>
            </div>
            <h2 className="mt-0.5 text-base font-black text-slate-900">{title}</h2>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to={managePath}
            className="rounded-xl border px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-white"
          >
            {manageLabel}
          </Link>
          <button
            type="button"
            onClick={onToggleForm}
            className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-bold transition ${
              showForm
                ? "bg-slate-200 text-slate-700 hover:bg-slate-300"
                : "bg-emerald-700 text-white hover:bg-emerald-800"
            }`}
          >
            {showForm ? (
              <><X className="h-3.5 w-3.5" /> Cancel</>
            ) : (
              <><Plus className="h-3.5 w-3.5" /> {addLabel}</>
            )}
          </button>
        </div>
      </div>

      {/* Inline form */}
      {showForm && (
        <div className="border-b bg-emerald-50/40 px-5 py-5">
          {formError && <LeaveError message={formError} />}
          {formContent}
        </div>
      )}

      {/* List */}
      <div className="px-5 py-4">{listContent}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Section 1 — Leave Year
// ─────────────────────────────────────────────────────────────────────────────
function LeaveYearSection({ onComplete, done }: { onComplete: () => void; done?: boolean }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<LeaveYearPolicy | null>(null);

  const { data, loading, error, reload } = useAsyncData(() => leaveApi.leaveYearPolicies(), []);

  const save = useMutation(async (payload: LeaveYearPolicyCreateRequest) => {
    if (editing) return leaveApi.updateLeaveYearPolicy(editing.id, payload);
    return leaveApi.createLeaveYearPolicy(payload);
  });

  async function handleSubmit(payload: LeaveYearPolicyCreateRequest) {
    await save.mutate(payload);
    setShowForm(false);
    setEditing(null);
    await reload();
    onComplete();
  }

  async function toggleActive(item: LeaveYearPolicy) {
    if (item.active) await leaveApi.deactivateLeaveYearPolicy(item.id);
    else await leaveApi.activateLeaveYearPolicy(item.id);
    await reload();
  }

  async function toggleClosed(item: LeaveYearPolicy) {
    if (item.closed) await leaveApi.reopenLeaveYearPolicy(item.id);
    else await leaveApi.closeLeaveYearPolicy(item.id);
    await reload();
  }

  const currentYear = new Date().getFullYear();
  const prefill: LeaveYearPolicyCreateRequest = editing
    ? { leaveYear: editing.leaveYear, startDate: editing.startDate, endDate: editing.endDate, active: editing.active, closed: editing.closed }
    : { leaveYear: currentYear, startDate: `${currentYear}-01-01`, endDate: `${currentYear}-12-31`, active: true, closed: false };

  return (
    <SectionCard
      step={1}
      icon={CalendarRange}
      title="Leave Year"
      description="Define the calendar year boundaries for leave entitlement and balances."
      done={done}
      managePath="/hr/leave/year-policies"
      manageLabel="Manage all →"
      showForm={showForm || editing !== null}
      onToggleForm={() => { setEditing(null); setShowForm((v) => !v); }}
      addLabel="New Leave Year"
      formError={save.error}
      formContent={
        <YearPolicyInlineForm
          prefill={prefill}
          submitting={save.loading}
          onSubmit={handleSubmit}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          isEdit={editing !== null}
        />
      }
      listContent={
        <>
          {loading && <LeaveLoading label="Loading leave years…" />}
          {error && <LeaveError message={error} onRetry={reload} />}
          {data && (
            data.length === 0 ? (
              <p className="text-sm text-slate-400">No leave years configured yet. Click "New Leave Year" to get started.</p>
            ) : (
              <div className="space-y-2">
                {data.slice(0, 5).map((item) => (
                  <div key={item.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-slate-50 px-4 py-2.5 text-sm">
                    <div className="flex items-center gap-3">
                      <span className="font-black text-slate-900">{item.leaveYear}</span>
                      <span className="text-slate-500">{item.startDate} → {item.endDate}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusPill active={item.active} trueLabel="Active" falseLabel="Inactive" />
                      {item.closed && <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-bold text-red-700">Closed</span>}
                      <button type="button" onClick={() => { setEditing(item); setShowForm(false); }}
                        className="rounded-lg border px-2.5 py-1 text-xs font-bold hover:bg-white">Edit</button>
                      <button type="button" onClick={() => toggleActive(item)}
                        className="rounded-lg border px-2.5 py-1 text-xs font-bold hover:bg-white">
                        {item.active ? "Deactivate" : "Activate"}
                      </button>
                      <button type="button" onClick={() => toggleClosed(item)}
                        className="rounded-lg border px-2.5 py-1 text-xs font-bold hover:bg-white">
                        {item.closed ? "Reopen" : "Close"}
                      </button>
                    </div>
                  </div>
                ))}
                {data.length > 5 && (
                  <Link to="/hr/leave/year-policies" className="block text-xs font-bold text-emerald-700 hover:underline">
                    View all {data.length} leave years →
                  </Link>
                )}
              </div>
            )
          )}
        </>
      }
    />
  );
}

function YearPolicyInlineForm({
  prefill,
  submitting,
  onSubmit,
  onCancel,
  isEdit,
}: {
  prefill: LeaveYearPolicyCreateRequest;
  submitting?: boolean;
  onSubmit: (payload: LeaveYearPolicyCreateRequest) => Promise<void>;
  onCancel: () => void;
  isEdit?: boolean;
}) {
  const [form, setForm] = useState<LeaveYearPolicyCreateRequest>(prefill);
  useEffect(() => { setForm(prefill); }, [JSON.stringify(prefill)]);

  function syncDates(year: number) {
    setForm((p) => ({ ...p, leaveYear: year, startDate: `${year}-01-01`, endDate: `${year}-12-31` }));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    await onSubmit({ ...form, leaveYear: Number(form.leaveYear) });
  }

  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-3">
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-slate-700">Year <span className="text-red-500">*</span></label>
        <input type="number" className="input" min={2000} max={2100} required
          value={form.leaveYear}
          onChange={(e) => syncDates(Number(e.target.value))} />
      </div>
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-slate-700">Start Date <span className="text-red-500">*</span></label>
        <input type="date" className="input" required value={form.startDate}
          onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))} />
      </div>
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-slate-700">End Date <span className="text-red-500">*</span></label>
        <input type="date" className="input" required value={form.endDate} min={form.startDate}
          onChange={(e) => setForm((p) => ({ ...p, endDate: e.target.value }))} />
      </div>
      <div className="flex flex-wrap items-center gap-3 sm:col-span-3">
        <label className={`flex cursor-pointer items-center gap-2.5 rounded-xl border px-3.5 py-2.5 text-sm font-semibold transition-colors ${form.active ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-700 hover:border-emerald-200 hover:bg-slate-50"}`}>
          <input type="checkbox" className="h-5 w-5 shrink-0 accent-emerald-600" checked={Boolean(form.active)}
            onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))} />
          Active
        </label>
        <label className={`flex cursor-pointer items-center gap-2.5 rounded-xl border px-3.5 py-2.5 text-sm font-semibold transition-colors ${form.closed ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-700 hover:border-emerald-200 hover:bg-slate-50"}`}>
          <input type="checkbox" className="h-5 w-5 shrink-0 accent-emerald-600" checked={Boolean(form.closed)}
            onChange={(e) => setForm((p) => ({ ...p, closed: e.target.checked }))} />
          Closed
        </label>
        <div className="ml-auto flex gap-2">
          <button type="button" onClick={onCancel}
            className="rounded-xl border px-4 py-2 text-sm font-bold hover:bg-white">Cancel</button>
          <button type="submit" disabled={submitting}
            className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800 disabled:opacity-60">
            {submitting ? "Saving…" : isEdit ? "Update Leave Year" : "Create Leave Year"}
          </button>
        </div>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Section 2 — Leave Types
// ─────────────────────────────────────────────────────────────────────────────
function LeaveTypesSection({ onComplete, done }: { onComplete: () => void; done?: boolean }) {
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<LeaveType | null>(null);

  const { data, loading, error, reload } = useAsyncData(() => leaveApi.allLeaveTypes(), []);

  const save = useMutation(async (payload: LeaveTypeCreateRequest) => {
    if (editing) return leaveApi.updateLeaveType(editing.id, payload);
    return leaveApi.createLeaveType(payload);
  });

  async function handleSubmit(payload: LeaveTypeCreateRequest) {
    await save.mutate(payload);
    setShowForm(false);
    setEditing(null);
    await reload();
    onComplete();
  }

  async function toggleActive(item: LeaveType) {
    if (!(await confirm(`${item.active ? "Deactivate" : "Activate"} "${item.name}"?`))) return;
    if (item.active) await leaveApi.deactivateLeaveType(item.id);
    else await leaveApi.activateLeaveType(item.id);
    await reload();
  }

  const genderLabel: Record<string, string> = { ALL: "All", MALE: "♂ Male", FEMALE: "♀ Female" };

  return (
    <SectionCard
      step={2}
      icon={Settings}
      title="Leave Types"
      description="Create every leave category your organisation uses — Annual Leave, Sick Leave, Study Leave, Maternity Leave, Paternity Leave, etc."
      done={done}
      managePath="/hr/leave/policies"
      manageLabel="Manage all →"
      showForm={showForm || editing !== null}
      onToggleForm={() => { setEditing(null); setShowForm((v) => !v); }}
      addLabel="New Leave Type"
      formError={save.error}
      formContent={
        <LeaveTypeForm
          initialValue={editing}
          submitting={save.loading}
          onSubmit={handleSubmit}
          onCancel={() => { setShowForm(false); setEditing(null); }}
        />
      }
      listContent={
        <>
          {loading && <LeaveLoading label="Loading leave types…" />}
          {error && <LeaveError message={error} onRetry={reload} />}
          {data && (
            data.length === 0 ? (
              <p className="text-sm text-slate-400">
                No leave types yet. Click <strong>"New Leave Type"</strong> above to create your first — Annual Leave, Sick Leave, Study Leave, etc.
              </p>
            ) : (
              <div className="space-y-2">
                {data.slice(0, 6).map((item) => (
                  <div key={item.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-slate-50 px-4 py-2.5 text-sm">
                    <div className="flex items-center gap-3">
                      <div>
                        <span className="font-black text-slate-900">{item.name}</span>
                        <span className="ml-2 text-xs text-slate-400">{item.code}</span>
                      </div>
                      {item.applicableGender !== "ALL" && (
                        <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${
                          item.applicableGender === "FEMALE" ? "bg-pink-100 text-pink-700" : "bg-blue-100 text-blue-700"
                        }`}>
                          {genderLabel[item.applicableGender]}
                        </span>
                      )}
                      <span className="text-xs text-slate-500">{item.annualEntitlementDays ?? 0} days/yr</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusPill active={item.active} trueLabel="Active" falseLabel="Inactive" />
                      <button type="button" onClick={() => { setEditing(item); setShowForm(false); }}
                        className="rounded-lg border px-2.5 py-1 text-xs font-bold hover:bg-white">Edit</button>
                      <button type="button" onClick={() => toggleActive(item)}
                        className="rounded-lg border px-2.5 py-1 text-xs font-bold hover:bg-white">
                        {item.active ? "Deactivate" : "Activate"}
                      </button>
                    </div>
                  </div>
                ))}
                {data.length > 6 && (
                  <Link to="/hr/leave/policies" className="block text-xs font-bold text-emerald-700 hover:underline">
                    View all {data.length} leave types →
                  </Link>
                )}
              </div>
            )
          )}
        </>
      }
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Section 3 — Entitlement Rules
// ─────────────────────────────────────────────────────────────────────────────
function EntitlementRulesSection({ onComplete, done }: { onComplete: () => void; done?: boolean }) {
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<LeaveEntitlementRule | null>(null);

  const rules = useAsyncData(() => leaveApi.entitlementRules(), []);
  // Lazy-load reference data only when form is open
  const leaveTypes = useAsyncData(() => (showForm || editing ? leaveApi.allLeaveTypes() : Promise.resolve(null)), [showForm, !!editing]);
  const employees = useAsyncData(() => (showForm || editing ? leaveApi.employeeOptions() : Promise.resolve(null)), [showForm, !!editing]);
  const genders = useAsyncData(() => (showForm || editing ? leaveApi.genders() : Promise.resolve(null)), [showForm, !!editing]);
  const empTypes = useAsyncData(() => (showForm || editing ? leaveApi.employmentTypes() : Promise.resolve(null)), [showForm, !!editing]);
  const empCats = useAsyncData(() => (showForm || editing ? leaveApi.employeeCategories() : Promise.resolve(null)), [showForm, !!editing]);

  const save = useMutation(async (payload: LeaveEntitlementRuleCreateRequest) => {
    if (editing) return leaveApi.updateEntitlementRule(editing.id, payload);
    return leaveApi.createEntitlementRule(payload);
  });

  async function handleSubmit(payload: LeaveEntitlementRuleCreateRequest) {
    await save.mutate(payload);
    setShowForm(false);
    setEditing(null);
    await rules.reload();
    onComplete();
  }

  async function toggleActive(item: LeaveEntitlementRule) {
    if (!(await confirm(`${item.active ? "Deactivate" : "Activate"} rule "${item.ruleName}"?`))) return;
    if (item.active) await leaveApi.deactivateEntitlementRule(item.id);
    else await leaveApi.activateEntitlementRule(item.id);
    await rules.reload();
  }

  const refReady = leaveTypes.data && employees.data && genders.data && empTypes.data && empCats.data;
  const refLoading = leaveTypes.loading || employees.loading || genders.loading || empTypes.loading || empCats.loading;

  return (
    <SectionCard
      step={3}
      icon={ListChecks}
      title="Entitlement Rules"
      description="Define how many days each employee is entitled to per leave type. Target by category, gender, employment type, department, or specific employee."
      done={done}
      managePath="/hr/leave/entitlement-rules"
      manageLabel="Manage all →"
      showForm={showForm || editing !== null}
      onToggleForm={() => { setEditing(null); setShowForm((v) => !v); }}
      addLabel="New Rule"
      formError={save.error}
      formContent={
        refLoading ? <LeaveLoading label="Loading reference data…" /> :
        refReady ? (
          <EntitlementRuleInlineForm
            initialValue={editing}
            leaveTypes={leaveTypes.data!}
            employees={employees.data!}
            genders={genders.data!}
            employmentTypes={empTypes.data!}
            employeeCategories={empCats.data!}
            submitting={save.loading}
            onSubmit={handleSubmit}
            onCancel={() => { setShowForm(false); setEditing(null); }}
          />
        ) : <LeaveLoading label="Loading…" />
      }
      listContent={
        <>
          {rules.loading && <LeaveLoading label="Loading rules…" />}
          {rules.error && <LeaveError message={rules.error} onRetry={rules.reload} />}
          {rules.data && (
            rules.data.length === 0 ? (
              <p className="text-sm text-slate-400">
                No entitlement rules yet. Rules tell the system how many days each employee is entitled to per leave type.
              </p>
            ) : (
              <div className="space-y-2">
                {rules.data.slice(0, 5).map((item) => (
                  <div key={item.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-slate-50 px-4 py-2.5 text-sm">
                    <div>
                      <span className="font-black text-slate-900">{item.ruleName}</span>
                      <span className="mx-2 text-slate-300">·</span>
                      <span className="text-slate-600">{item.leaveTypeName}</span>
                      <span className="mx-2 text-slate-300">·</span>
                      <span className="font-bold text-emerald-700">{item.entitledDays} days</span>
                      {(item.employeeCategoryName || item.genderName || item.employmentTypeName) && (
                        <span className="ml-2 text-xs text-slate-400">
                          ({[item.employeeCategoryName, item.genderName, item.employmentTypeName].filter(Boolean).join(", ")})
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusPill active={item.active} trueLabel="Active" falseLabel="Inactive" />
                      <button type="button" onClick={() => { setEditing(item); setShowForm(false); }}
                        className="rounded-lg border px-2.5 py-1 text-xs font-bold hover:bg-white">Edit</button>
                      <button type="button" onClick={() => toggleActive(item)}
                        className="rounded-lg border px-2.5 py-1 text-xs font-bold hover:bg-white">
                        {item.active ? "Deactivate" : "Activate"}
                      </button>
                    </div>
                  </div>
                ))}
                {rules.data.length > 5 && (
                  <Link to="/hr/leave/entitlement-rules" className="block text-xs font-bold text-emerald-700 hover:underline">
                    View all {rules.data.length} rules →
                  </Link>
                )}
              </div>
            )
          )}
        </>
      }
    />
  );
}

function EntitlementRuleInlineForm({
  initialValue,
  leaveTypes,
  employees,
  genders,
  employmentTypes,
  employeeCategories,
  submitting,
  onSubmit,
  onCancel,
}: {
  initialValue?: LeaveEntitlementRule | null;
  leaveTypes: LeaveType[];
  employees: EmployeeOption[];
  genders: RefLookup[];
  employmentTypes: RefLookup[];
  employeeCategories: RefLookup[];
  submitting?: boolean;
  onSubmit: (payload: LeaveEntitlementRuleCreateRequest) => Promise<void>;
  onCancel: () => void;
}) {
  const toast = useToast();
  const [form, setForm] = useState<LeaveEntitlementRuleCreateRequest>({
    leaveTypeId: 0, ruleName: "", employeeId: null, roleId: null, departmentId: null,
    employeeCategoryId: null, genderId: null, employmentTypeId: null,
    entitledDays: 0, maxDaysPerRequest: null, requiresAttachment: null,
    dayCountMode: "WORKING_DAYS", excludeWeekends: true, excludePublicHolidays: true,
    priority: 100, active: true,
  });

  useEffect(() => {
    if (initialValue) {
      setForm({
        leaveTypeId: initialValue.leaveTypeId, ruleName: initialValue.ruleName,
        employeeId: initialValue.employeeId ?? null, roleId: null, departmentId: null,
        employeeCategoryId: initialValue.employeeCategoryId ?? null,
        genderId: initialValue.genderId ?? null,
        employmentTypeId: initialValue.employmentTypeId ?? null,
        entitledDays: initialValue.entitledDays,
        maxDaysPerRequest: initialValue.maxDaysPerRequest ?? null,
        requiresAttachment: initialValue.requiresAttachment ?? null,
        dayCountMode: initialValue.dayCountMode || "WORKING_DAYS",
        excludeWeekends: initialValue.excludeWeekends ?? true,
        excludePublicHolidays: initialValue.excludePublicHolidays ?? true,
        priority: initialValue.priority, active: initialValue.active,
      });
    }
  }, [initialValue?.id]);

  function set<K extends keyof LeaveEntitlementRuleCreateRequest>(k: K, v: LeaveEntitlementRuleCreateRequest[K]) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!form.leaveTypeId) { toast.error("Please select a leave type."); return; }
    if (!form.ruleName.trim()) { toast.error("Please enter a rule name."); return; }
    await onSubmit({ ...form, ruleName: form.ruleName.trim(), entitledDays: Number(form.entitledDays), priority: Number(form.priority || 100) });
  }

  const empLabel = useMemo(() => (e: EmployeeOption) =>
    e.employeeName || e.fullName || e.name || e.employeeNumber, []);

  // Picking a Leave Type pre-fills its own Day Count Mode / Exclude Weekends / Exclude
  // Public Holidays / Max Days / Requires Attachment as this rule's starting point, instead
  // of the previous hardcoded "Working Days, both boxes checked" regardless of which leave
  // type was chosen. Only applies when CREATING a new rule -- editing an existing rule keeps
  // that rule's own saved values (already loaded by the initialValue effect above), since a
  // saved override should never be silently clobbered just by the leave type re-rendering.
  function handleLeaveTypeChange(id: number) {
    set("leaveTypeId", id);
    if (initialValue) return;
    const selected = leaveTypes.find((t) => t.id === id);
    if (!selected) return;
    setForm((prev) => ({
      ...prev,
      leaveTypeId: id,
      dayCountMode: selected.dayCountMode,
      excludeWeekends: selected.excludeWeekends,
      excludePublicHolidays: selected.excludePublicHolidays,
      maxDaysPerRequest: selected.maxDaysPerRequest ?? null,
      requiresAttachment: selected.requiresAttachment,
    }));
  }

  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-slate-700">Leave Type <span className="text-red-500">*</span></label>
        <select className="input" value={form.leaveTypeId || ""} required
          onChange={(e) => handleLeaveTypeChange(Number(e.target.value))}>
          <option value="">Select leave type</option>
          {leaveTypes.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.code})</option>)}
        </select>
      </div>
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-slate-700">Rule Name <span className="text-red-500">*</span></label>
        <input className="input" placeholder="e.g. All staff annual leave" required value={form.ruleName}
          onChange={(e) => set("ruleName", e.target.value)} />
      </div>
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-slate-700">Entitled Days <span className="text-red-500">*</span></label>
        <input type="number" min={0} step="0.5" className="input" required value={form.entitledDays}
          onChange={(e) => set("entitledDays", Number(e.target.value))} />
      </div>
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-slate-700">Max Days / Request</label>
        <input type="number" min={0} step="0.5" className="input" value={form.maxDaysPerRequest ?? ""}
          onChange={(e) => set("maxDaysPerRequest", e.target.value ? Number(e.target.value) : null)} />
      </div>
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-slate-700">Employee Category</label>
        <select className="input" value={form.employeeCategoryId ?? ""}
          onChange={(e) => set("employeeCategoryId", e.target.value ? Number(e.target.value) : null)}>
          <option value="">Any category</option>
          {employeeCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-slate-700">Gender</label>
        <select className="input" value={form.genderId ?? ""}
          onChange={(e) => set("genderId", e.target.value ? Number(e.target.value) : null)}>
          <option value="">Any gender</option>
          {genders.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      </div>
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-slate-700">Employment Type</label>
        <select className="input" value={form.employmentTypeId ?? ""}
          onChange={(e) => set("employmentTypeId", e.target.value ? Number(e.target.value) : null)}>
          <option value="">Any</option>
          {employmentTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-slate-700">Specific Employee</label>
        <select className="input" value={form.employeeId ?? ""}
          onChange={(e) => set("employeeId", e.target.value ? Number(e.target.value) : null)}>
          <option value="">Any employee</option>
          {employees.map((e) => <option key={e.id} value={e.id}>{empLabel(e)} — {e.employeeNumber}</option>)}
        </select>
      </div>
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-slate-700">Day Count Mode</label>
        <select className="input" value={form.dayCountMode || "WORKING_DAYS"}
          onChange={(e) => set("dayCountMode", e.target.value as LeaveDayCountMode)}>
          <option value="WORKING_DAYS">Working Days</option>
          <option value="CALENDAR_DAYS">Calendar Days</option>
        </select>
      </div>
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-slate-700">Priority</label>
        <input type="number" min={1} className="input" value={form.priority ?? 100}
          onChange={(e) => set("priority", Number(e.target.value))} />
      </div>
      {form.dayCountMode === "CALENDAR_DAYS" && (
        <div className="rounded-xl bg-slate-50 px-4 py-2.5 text-xs text-slate-500 sm:col-span-2">
          Calendar Days counts every day in the range, including weekends and
          holidays — "Exclude Weekends"/"Exclude Holidays" only apply in
          Working Days mode.
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
        {[
          ...(form.dayCountMode === "CALENDAR_DAYS"
            ? []
            : [
                { key: "excludeWeekends" as const, label: "Exclude Weekends" },
                { key: "excludePublicHolidays" as const, label: "Exclude Holidays" },
              ]),
          { key: "active" as const, label: "Active" },
        ].map(({ key, label }) => (
          <label key={key} className={`flex cursor-pointer items-center gap-2.5 rounded-xl border px-3.5 py-2.5 text-sm font-semibold transition-colors ${form[key] ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-700 hover:border-emerald-200 hover:bg-slate-50"}`}>
            <input type="checkbox" className="h-5 w-5 shrink-0 accent-emerald-600" checked={Boolean(form[key])}
              onChange={(e) => set(key, e.target.checked)} />
            {label}
          </label>
        ))}
        <div className="ml-auto flex gap-2">
          <button type="button" onClick={onCancel}
            className="rounded-xl border px-4 py-2 text-sm font-bold hover:bg-white">Cancel</button>
          <button type="submit" disabled={submitting}
            className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800 disabled:opacity-60">
            {submitting ? "Saving…" : initialValue ? "Update Rule" : "Create Rule"}
          </button>
        </div>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Section 4 — Holiday Calendar
// ─────────────────────────────────────────────────────────────────────────────
function HolidaysSection({ onComplete, done }: { onComplete: () => void; done?: boolean }) {
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Holiday | null>(null);

  const { data, loading, error, reload } = useAsyncData(() => leaveApi.holidays(0, 50), []);

  const save = useMutation(async (payload: HolidayCreateRequest) => {
    if (editing) return leaveApi.updateHoliday(editing.id, payload);
    return leaveApi.createHoliday(payload);
  });

  async function handleSubmit(payload: HolidayCreateRequest) {
    await save.mutate(payload);
    setShowForm(false);
    setEditing(null);
    await reload();
    onComplete();
  }

  async function toggleActive(item: Holiday) {
    if (!(await confirm(`${item.active ? "Deactivate" : "Activate"} "${item.name}"?`))) return;
    if (item.active) await leaveApi.deactivateHoliday(item.id);
    else await leaveApi.activateHoliday(item.id);
    await reload();
  }

  return (
    <SectionCard
      step={4}
      icon={CalendarDays}
      title="Holiday Calendar"
      description="Add public holidays that are automatically excluded when calculating leave days."
      done={done}
      managePath="/hr/leave/holidays"
      manageLabel="Manage all →"
      showForm={showForm || editing !== null}
      onToggleForm={() => { setEditing(null); setShowForm((v) => !v); }}
      addLabel="Add Holiday"
      formError={save.error}
      formContent={
        <HolidayInlineForm
          initialValue={editing}
          submitting={save.loading}
          onSubmit={handleSubmit}
          onCancel={() => { setShowForm(false); setEditing(null); }}
        />
      }
      listContent={
        <>
          {loading && <LeaveLoading label="Loading holidays…" />}
          {error && <LeaveError message={error} onRetry={reload} />}
          {data && (
            data.content.length === 0 ? (
              <p className="text-sm text-slate-400">
                No holidays configured yet. Add public holidays to ensure they are excluded from leave day counts.
              </p>
            ) : (
              <div className="space-y-2">
                {data.content.slice(0, 6).map((item) => (
                  <div key={item.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-slate-50 px-4 py-2.5 text-sm">
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-bold text-slate-700">{item.holidayDate}</span>
                      <span className="font-black text-slate-900">{item.name}</span>
                      {item.recurringAnnually && (
                        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-bold text-blue-700">Recurring</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusPill active={item.active} trueLabel="Active" falseLabel="Inactive" />
                      <button type="button" onClick={() => { setEditing(item); setShowForm(false); }}
                        className="rounded-lg border px-2.5 py-1 text-xs font-bold hover:bg-white">Edit</button>
                      <button type="button" onClick={() => toggleActive(item)}
                        className="rounded-lg border px-2.5 py-1 text-xs font-bold hover:bg-white">
                        {item.active ? "Deactivate" : "Activate"}
                      </button>
                    </div>
                  </div>
                ))}
                {data.totalElements > 6 && (
                  <Link to="/hr/leave/holidays" className="block text-xs font-bold text-emerald-700 hover:underline">
                    View all {data.totalElements} holidays →
                  </Link>
                )}
              </div>
            )
          )}
        </>
      }
    />
  );
}

function HolidayInlineForm({
  initialValue,
  submitting,
  onSubmit,
  onCancel,
}: {
  initialValue?: Holiday | null;
  submitting?: boolean;
  onSubmit: (payload: HolidayCreateRequest) => Promise<void>;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<HolidayCreateRequest>({
    holidayDate: "", name: "", description: "", recurringAnnually: false, active: true,
  });

  useEffect(() => {
    if (initialValue) {
      setForm({ holidayDate: initialValue.holidayDate, name: initialValue.name,
        description: initialValue.description || "", recurringAnnually: initialValue.recurringAnnually, active: initialValue.active });
    }
  }, [initialValue?.id]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    await onSubmit({ ...form, name: form.name.trim(), description: form.description?.trim() || null });
  }

  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-3">
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-slate-700">Date <span className="text-red-500">*</span></label>
        <input type="date" className="input" required value={form.holidayDate}
          onChange={(e) => setForm((p) => ({ ...p, holidayDate: e.target.value }))} />
      </div>
      <div className="space-y-1.5 sm:col-span-2">
        <label className="text-xs font-semibold text-slate-700">Holiday Name <span className="text-red-500">*</span></label>
        <input className="input" placeholder="e.g. Madaraka Day" required value={form.name}
          onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
      </div>
      <div className="space-y-1.5 sm:col-span-3">
        <label className="text-xs font-semibold text-slate-700">Description (optional)</label>
        <input className="input" placeholder="Optional notes" value={form.description || ""}
          onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
      </div>
      <div className="flex flex-wrap items-center gap-3 sm:col-span-3">
        <label className={`flex cursor-pointer items-center gap-2.5 rounded-xl border px-3.5 py-2.5 text-sm font-semibold transition-colors ${form.recurringAnnually ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-700 hover:border-emerald-200 hover:bg-slate-50"}`}>
          <input type="checkbox" className="h-5 w-5 shrink-0 accent-emerald-600" checked={Boolean(form.recurringAnnually)}
            onChange={(e) => setForm((p) => ({ ...p, recurringAnnually: e.target.checked }))} />
          Recurring Annually
        </label>
        <label className={`flex cursor-pointer items-center gap-2.5 rounded-xl border px-3.5 py-2.5 text-sm font-semibold transition-colors ${form.active ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-700 hover:border-emerald-200 hover:bg-slate-50"}`}>
          <input type="checkbox" className="h-5 w-5 shrink-0 accent-emerald-600" checked={Boolean(form.active)}
            onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))} />
          Active
        </label>
        <div className="ml-auto flex gap-2">
          <button type="button" onClick={onCancel}
            className="rounded-xl border px-4 py-2 text-sm font-bold hover:bg-white">Cancel</button>
          <button type="submit" disabled={submitting}
            className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800 disabled:opacity-60">
            {submitting ? "Saving…" : initialValue ? "Update Holiday" : "Add Holiday"}
          </button>
        </div>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared helpers
// ─────────────────────────────────────────────────────────────────────────────
function StatusPill({ active, trueLabel, falseLabel }: { active: boolean; trueLabel: string; falseLabel: string }) {
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${
      active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"
    }`}>
      {active ? trueLabel : falseLabel}
    </span>
  );
}
